import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, money, apiErr } from "@/lib/api";
import { Button, Field, Input, Badge } from "@/lib/ui";
import { useAuth } from "@/context/Auth";

const TABS = [
  { k: "badge", t: "Seller Badge", i: "fa-certificate" },
  { k: "billing", t: "Billing", i: "fa-receipt" },
  { k: "password", t: "Change Password", i: "fa-key" },
  { k: "support", t: "Support", i: "fa-headset" },
];

export default function SellerExtras() {
  const { user } = useAuth();
  const [tab, setTab] = useState("badge");
  const [seller, setSeller] = useState(null);
  const [payments, setPayments] = useState([]);
  const [sup, setSup] = useState({ subject: "", message: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  useEffect(() => {
    api.get("/seller/me").then(({ data }) => setSeller(data));
    api.get("/seller/payments").then(({ data }) => setPayments(data));
  }, []);

  const instaBuy = null;
  const sendSupport = async (e) => {
    e.preventDefault();
    try { await api.post("/seller/support", sup); toast.success("Query raised — response in 3–5 working days."); setSup({ subject: "", message: "" }); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/seller/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); }
    catch (er) { toast.error(apiErr(er)); }
  };

  const badge = seller?.badge || {};

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">More</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`extras-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${tab === t.k ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
            <i className={`fa-${t.brand ? "brands" : "solid"} ${t.i} mr-1`} /> {t.t}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "badge" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-certificate text-3xl text-emerald-500" />
              <div>
                <p className="font-head text-lg font-extrabold text-slate-900">Seller Badge</p>
                <p className="text-sm text-slate-500">A verified badge shown next to your company on buyer listings.</p>
              </div>
              <div className="ml-auto"><Badge tone={badge.status === "active" ? "green" : "slate"}>{badge.status || "inactive"}</Badge></div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <Cell label="Badge Type" value={badge.type || "—"} />
              <Cell label="Activation" value={badge.activation ? new Date(badge.activation).toLocaleDateString() : "—"} />
              <Cell label="Expiry" value={badge.expiry ? new Date(badge.expiry).toLocaleDateString() : "—"} />
              <Cell label="Status" value={badge.status || "inactive"} />
            </div>
            {badge.status !== "active" && <p className="mt-4 text-xs text-slate-400">Seller Badge is admin-controlled. Contact support to request activation.</p>}
          </div>
        )}

        {tab === "billing" && (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-slate-50 text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-3">Type</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payments.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No billing history yet</td></tr> :
                  payments.map((p) => (
                    <tr key={p.id}><td className="px-4 py-3 capitalize">{p.type}</td><td className="px-4 py-3">{p.plan_name}</td>
                      <td className="px-4 py-3 font-semibold">{money(p.amount)}</td><td className="px-4 py-3"><Badge tone={p.status === "approved" ? "green" : p.status === "rejected" ? "red" : "amber"}>{p.status}</Badge></td>
                      <td className="px-4 py-3 text-slate-500">{new Date(p.created_at).toLocaleDateString()}</td></tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "password" && (
          <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6">
            <p className="font-head text-lg font-extrabold text-slate-900">Change Password</p>
            <form onSubmit={changePw} className="mt-4 space-y-3">
              <Field label="Current Password" required><Input type="password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} data-testid="pw-current" /></Field>
              <Field label="New Password" required hint="At least 6 characters, not your mobile number."><Input type="password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} data-testid="pw-new" /></Field>
              <Button type="submit" data-testid="pw-submit">Update Password</Button>
            </form>
          </div>
        )}

        {tab === "support" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <p className="font-head text-lg font-extrabold text-slate-900">Need Help? Raise a Query</p>
            <p className="text-sm text-slate-500">connect.deadstock@gmail.com · Response target: 3–5 working days.</p>
            <form onSubmit={sendSupport} className="mt-4 space-y-3">
              <Field label="Subject" required><Input value={sup.subject} onChange={(e) => setSup((s) => ({ ...s, subject: e.target.value }))} data-testid="support-subject" /></Field>
              <Field label="Message" required>
                <textarea value={sup.message} onChange={(e) => setSup((s) => ({ ...s, message: e.target.value }))} rows={4}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange-500" data-testid="support-message" />
              </Field>
              <Button type="submit" data-testid="support-submit">Submit Query</Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({ label, value }) {
  return <div className="rounded-lg border border-slate-200 p-3"><p className="text-xs uppercase tracking-wider text-slate-400">{label}</p><p className="mt-0.5 font-semibold capitalize text-slate-800">{value}</p></div>;
}
